import usePageTitle from "../utils/usePageTitle";
import React, { useEffect, useMemo, useState } from "react";
import { Container, Select, Switch, TextInput } from "common-tailwind-components";
import { Formik, type FormikHelpers } from "formik";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LogIn, Mail, Pencil, Save, ShieldCheck, X } from "lucide-react";

import {
  companyRouterFetch,
  create_company,
  create_user,
  enroll_user,
  formatApiRequestError,
  get_companies,
  get_company_api_url,
  get_users,
  splitApiRequestFormErrors,
  update_company,
  update_user,
} from "../api";
import { useLogin } from "../reducers";
import type { Company, User } from "../api/types";
import { IS_COMPANY_ROUTER_FRONTEND } from "../globals";
import { canManageUsers, canSeeExecutiveDashboard, isAdmin } from "../utils/permissions";

type CreateUserForm = {
  username: string;
  password: string;
  email: string;
  first_name: string;
  last_name: string;
  company_id: number | "";
};

type EditUserForm = {
  email: string;
  first_name: string;
  last_name: string;
  password: string;
  confirm_password: string;
};

type EditCompanyForm = {
  name: string;
  logo_url: string;
  allow_password_login: boolean;
};

type EnrollUserForm = {
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  company_id: number | "";
};

type CreateCompanyManagementForm = {
  id: string;
  name: string;
  logo_url: string;
  allow_password_login: boolean;
};

const emptyForm: CreateUserForm = {
  username: "",
  password: "",
  email: "",
  first_name: "",
  last_name: "",
  company_id: "",
};

const emptyCompanyForm: CreateCompanyManagementForm = {
  id: "",
  name: "",
  logo_url: "",
  allow_password_login: true,
};

const emptyEnrollForm: EnrollUserForm = {
  username: "",
  email: "",
  first_name: "",
  last_name: "",
  company_id: "",
};

const userToEditForm = (user: User): EditUserForm => ({
  email: user.email ?? "",
  first_name: user.first_name ?? "",
  last_name: user.last_name ?? "",
  password: "",
  confirm_password: "",
});

const companyToEditForm = (company: Company): EditCompanyForm => ({
  name: company.name,
  logo_url: company.logo_url ?? "",
  allow_password_login: company.allow_password_login,
});

const UserManagement = () => {
  const { t } = useTranslation();
  const { permissions, user: currentUser } = useLogin();
  const [users, setUsers] = useState<User[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [companyError, setCompanyError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitBusy, setSubmitBusy] = useState(false);
  const [enrollError, setEnrollError] = useState<string | null>(null);
  const [enrollSuccess, setEnrollSuccess] = useState<string | null>(null);
  const [enrollBusy, setEnrollBusy] = useState(false);
  const [createCompanyError, setCreateCompanyError] = useState<string | null>(null);
  const [createCompanySuccess, setCreateCompanySuccess] = useState<string | null>(null);
  const [createCompanyBusy, setCreateCompanyBusy] = useState(false);
  const [defaultCompanyId, setDefaultCompanyId] = useState<number | "">("");
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<EditUserForm | null>(null);
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editingCompanyId, setEditingCompanyId] = useState<number | null>(null);
  const [editCompanyForm, setEditCompanyForm] = useState<EditCompanyForm | null>(null);
  const [editCompanyBusy, setEditCompanyBusy] = useState(false);
  const [editCompanyError, setEditCompanyError] = useState<string | null>(null);
  const [existingEnrollmentUser, setExistingEnrollmentUser] = useState<User | null>(null);
  const [existingEnrollmentBusy, setExistingEnrollmentBusy] = useState(false);
  const [existingEnrollmentError, setExistingEnrollmentError] = useState<string | null>(null);
  const [existingEnrollmentSuccess, setExistingEnrollmentSuccess] = useState<string | null>(null);
  const [impersonationBusy, setImpersonationBusy] = useState(false);
  const [impersonationError, setImpersonationError] = useState<string | null>(null);

  usePageTitle(t("user_management"));

  const canCreateUsers = canSeeExecutiveDashboard(permissions);
  const canEditUsers = canManageUsers(permissions);
  const canChooseCompany = isAdmin(permissions);
  const sortedUsers = useMemo(
    () => [...users].sort((a, b) => a.username.localeCompare(b.username)),
    [users],
  );
  const sortedCompanies = useMemo(
    () => [...companies].sort((a, b) => a.name.localeCompare(b.name)),
    [companies],
  );
  const canImpersonateFromThisFrontend = canChooseCompany && IS_COMPANY_ROUTER_FRONTEND;
  const impersonationAction = canImpersonateFromThisFrontend
    ? `${get_company_api_url()}/api/clank/user/impersonate/`
    : undefined;
  const impersonationRedirect =
    typeof window === "undefined" ? "http://127.0.0.1/" : window.location.href;

  const onImpersonationSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!impersonationAction) {
      return;
    }

    setImpersonationBusy(true);
    setImpersonationError(null);
    try {
      const body = new URLSearchParams();
      new FormData(event.currentTarget).forEach((value, key) => {
        if (typeof value === "string") {
          body.append(key, value);
        }
      });
      const response = await companyRouterFetch(impersonationAction, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => null);
        throw error ?? new Error(`Request failed with status ${response.status}`);
      }

      window.location.assign(impersonationRedirect);
    } catch (err: unknown) {
      setImpersonationError(formatApiRequestError(err, t));
      setImpersonationBusy(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    get_users()
      .then(data => {
        if (!cancelled) {
          setUsers(data.items);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : t("failed_to_load_users"));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [t]);

  useEffect(() => {
    if (!canChooseCompany) {
      setCompanies([]);
      setCompanyError(null);
      setCompaniesLoading(false);
      setDefaultCompanyId("");
      return;
    }

    let cancelled = false;
    setCompaniesLoading(true);
    get_companies()
      .then(data => {
        if (cancelled) {
          return;
        }

        setCompanies(data.items);
        setCompanyError(null);
        setDefaultCompanyId(prev => {
          if (prev !== "" || data.items.length === 0) {
            return prev;
          }
          const currentCompany =
            currentUser.company_id != null
              ? data.items.find(company => company.id === currentUser.company_id)
              : undefined;
          return (currentCompany ?? data.items[0]).id;
        });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setCompanyError(err instanceof Error ? err.message : t("failed_to_load_companies"));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setCompaniesLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [canChooseCompany, currentUser.company_id, t]);

  const onSubmit = async (values: CreateUserForm, helpers: FormikHelpers<CreateUserForm>) => {
    if (!canCreateUsers) {
      setSubmitError(t("you_do_not_have_permission_to_create_users"));
      return;
    }

    setSubmitBusy(true);
    setSubmitError(null);
    helpers.setErrors({});
    try {
      const companyId = canChooseCompany ? values.company_id : currentUser.company_id;
      const created = await create_user({
        username: values.username.trim(),
        password: values.password,
        company_id: companyId as number,
        email: values.email.trim() || null,
        first_name: values.first_name.trim() || null,
        last_name: values.last_name.trim() || null,
      });
      setUsers(prev => [created, ...prev.filter(user => user.id !== created.id)]);
      helpers.resetForm();
    } catch (err: unknown) {
      const parsed = splitApiRequestFormErrors<CreateUserForm>(err, t, [
        "username",
        "password",
        "email",
        "first_name",
        "last_name",
        "company_id",
      ]);
      helpers.setTouched(parsed.touched, false);
      helpers.setErrors(parsed.fieldErrors);
      setSubmitError(parsed.remainder);
    } finally {
      setSubmitBusy(false);
    }
  };

  const onEnrollSubmit = async (values: EnrollUserForm, helpers: FormikHelpers<EnrollUserForm>) => {
    if (!canCreateUsers) {
      setEnrollError(t("you_do_not_have_permission_to_create_users"));
      return;
    }

    const companyId = canChooseCompany ? values.company_id : currentUser.company_id;
    setEnrollBusy(true);
    setEnrollError(null);
    setEnrollSuccess(null);
    setExistingEnrollmentSuccess(null);
    helpers.setErrors({});
    try {
      const enrolled = await enroll_user({
        NewUser: {
          username: values.username.trim(),
          email: values.email.trim(),
          first_name: values.first_name.trim(),
          last_name: values.last_name.trim(),
          company_id: companyId as number,
        },
      });
      setUsers(prev => [enrolled, ...prev.filter(user => user.id !== enrolled.id)]);
      setEnrollSuccess(t("user_enrollment_sent"));
      helpers.resetForm();
    } catch (err: unknown) {
      const parsed = splitApiRequestFormErrors<EnrollUserForm>(
        err,
        t,
        ["username", "email", "first_name", "last_name", "company_id"],
        {
          "NewUser.username": "username",
          "NewUser.email": "email",
          "NewUser.first_name": "first_name",
          "NewUser.last_name": "last_name",
          "NewUser.company_id": "company_id",
        },
      );
      helpers.setTouched(parsed.touched, false);
      helpers.setErrors(parsed.fieldErrors);
      setEnrollError(parsed.remainder);
    } finally {
      setEnrollBusy(false);
    }
  };

  const onCreateCompanySubmit = async (
    values: CreateCompanyManagementForm,
    helpers: FormikHelpers<CreateCompanyManagementForm>,
  ) => {
    if (!canChooseCompany) {
      setCreateCompanyError(t("you_do_not_have_permission_to_create_companies"));
      return;
    }

    setCreateCompanyBusy(true);
    setCreateCompanyError(null);
    setCreateCompanySuccess(null);
    helpers.setErrors({});
    try {
      const created = await create_company({
        id: Number(values.id),
        name: values.name.trim(),
        logo_url: values.logo_url.trim() || null,
        allow_password_login: values.allow_password_login,
      });
      setCompanies(prev => [created, ...prev.filter(company => company.id !== created.id)]);
      setDefaultCompanyId(created.id);
      setCreateCompanySuccess(t("company_created"));
      helpers.resetForm();
    } catch (err: unknown) {
      const parsed = splitApiRequestFormErrors<CreateCompanyManagementForm>(err, t, [
        "id",
        "name",
        "logo_url",
        "allow_password_login",
      ]);
      helpers.setTouched(parsed.touched, false);
      helpers.setErrors(parsed.fieldErrors);
      setCreateCompanyError(parsed.remainder);
    } finally {
      setCreateCompanyBusy(false);
    }
  };

  const startEditing = (user: User) => {
    setEditingUserId(user.id);
    setEditForm(userToEditForm(user));
    setEditError(null);
  };

  const cancelEditing = () => {
    setEditingUserId(null);
    setEditForm(null);
    setEditError(null);
  };

  const startEditingCompany = (company: Company) => {
    setEditingCompanyId(company.id);
    setEditCompanyForm(companyToEditForm(company));
    setEditCompanyError(null);
  };

  const cancelEditingCompany = () => {
    setEditingCompanyId(null);
    setEditCompanyForm(null);
    setEditCompanyError(null);
  };

  const onEditCompanySubmit = async (event: React.FormEvent<HTMLFormElement>, company: Company) => {
    event.preventDefault();
    if (!canChooseCompany || editCompanyForm === null) {
      setEditCompanyError(t("you_do_not_have_permission_to_edit_companies"));
      return;
    }

    setEditCompanyBusy(true);
    setEditCompanyError(null);
    try {
      const updated = await update_company(company.id, {
        name: editCompanyForm.name.trim(),
        logo_url: editCompanyForm.logo_url.trim() || null,
        allow_password_login: editCompanyForm.allow_password_login,
      });
      setCompanies(prev => prev.map(company => (company.id === updated.id ? updated : company)));
      cancelEditingCompany();
    } catch (err: unknown) {
      setEditCompanyError(formatApiRequestError(err, t));
    } finally {
      setEditCompanyBusy(false);
    }
  };

  const openExistingEnrollment = (user: User) => {
    setExistingEnrollmentUser(user);
    setExistingEnrollmentError(null);
    setExistingEnrollmentSuccess(null);
  };

  const closeExistingEnrollment = () => {
    if (existingEnrollmentBusy) {
      return;
    }

    setExistingEnrollmentUser(null);
    setExistingEnrollmentError(null);
  };

  const confirmExistingEnrollment = async () => {
    if (!existingEnrollmentUser) {
      return;
    }

    setExistingEnrollmentBusy(true);
    setExistingEnrollmentError(null);
    setExistingEnrollmentSuccess(null);
    try {
      const enrolled = await enroll_user({
        Existing: {
          id: existingEnrollmentUser.id,
        },
      });
      setUsers(prev => prev.map(user => (user.id === enrolled.id ? enrolled : user)));
      setExistingEnrollmentSuccess(t("user_enrollment_sent"));
      setExistingEnrollmentUser(null);
    } catch (err: unknown) {
      setExistingEnrollmentError(formatApiRequestError(err, t));
    } finally {
      setExistingEnrollmentBusy(false);
    }
  };

  const onEditSubmit = async (event: React.FormEvent<HTMLFormElement>, user: User) => {
    event.preventDefault();
    if (!canEditUsers || editForm === null) {
      setEditError(t("you_do_not_have_permission_to_edit_users"));
      return;
    }

    if (editForm.password !== editForm.confirm_password) {
      setEditError(t("passwords_do_not_match"));
      return;
    }

    setEditBusy(true);
    setEditError(null);
    try {
      const email = editForm.email.trim();
      const firstName = editForm.first_name.trim();
      const lastName = editForm.last_name.trim();
      const updated = await update_user(user.id, {
        email: email || null,
        first_name: firstName || null,
        last_name: lastName || null,
        ...(editForm.password ? { password: editForm.password } : {}),
      });
      setUsers(prev => prev.map(user => (user.id === updated.id ? updated : user)));
      cancelEditing();
    } catch (err: unknown) {
      setEditError(formatApiRequestError(err, t));
    } finally {
      setEditBusy(false);
    }
  };

  return (
    <Container>
      <div className="mx-auto flex max-w-6xl flex-col gap-6 px-4 pb-8 pt-7">
        <div className="space-y-2">
          <Link
            to="/administration/"
            className="inline-flex text-sm text-primary transition hover:text-primary/80"
          >
            {t("back_to_executive_dashboard")}
          </Link>
          {/* No heading: the toolbar names the page and stays put. */}
          <p className="text-sm text-text-dim">{t("user_management_description")}</p>
        </div>

        <div className="flex flex-col gap-6">
          {canChooseCompany && (
            <section className="rounded-2xl border border-outline bg-surface p-5 shadow-sm">
              <div className="mb-4 flex items-center justify-between gap-3">
                <h2 className="text-xl font-semibold text-text">{t("companies")}</h2>
                <div className="text-sm text-text-dim">
                  {companiesLoading
                    ? t("loading_companies")
                    : t("companies_count", { count: sortedCompanies.length })}
                </div>
              </div>

              {companyError ? (
                <div className="rounded-xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
                  {companyError}
                </div>
              ) : companiesLoading ? (
                <div className="text-sm text-text-dim">{t("loading_companies")}</div>
              ) : (
                <div className="overflow-hidden rounded-xl border border-outline">
                  <table className="min-w-full divide-y divide-outline text-sm">
                    <thead className="bg-muted-surface text-left text-text-dim">
                      <tr>
                        <th className="px-4 py-3 font-medium">{t("company_id")}</th>
                        <th className="px-4 py-3 font-medium">{t("company_name")}</th>
                        <th className="px-4 py-3 font-medium">{t("company_logo_url")}</th>
                        <th className="px-4 py-3 font-medium">{t("allow_password_login")}</th>
                        <th className="px-4 py-3 text-right font-medium">{t("actions")}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-outline bg-surface">
                      {sortedCompanies.map(company => {
                        const isEditing =
                          editingCompanyId === company.id && editCompanyForm !== null;

                        return (
                          <tr key={company.id}>
                            {isEditing ? (
                              <td colSpan={5} className="px-4 py-4">
                                <form
                                  className="grid gap-3 lg:grid-cols-[0.5fr_1fr_1.5fr_1fr_auto]"
                                  onSubmit={event => onEditCompanySubmit(event, company)}
                                >
                                  <div className="space-y-1">
                                    <span className="text-xs font-medium uppercase text-text-dim">
                                      {t("company_id")}
                                    </span>
                                    <div className="rounded-xl border border-outline bg-muted-surface px-3 py-2 text-sm text-text-dim">
                                      {company.id}
                                    </div>
                                  </div>
                                  <label className="space-y-1">
                                    <span className="text-xs font-medium uppercase text-text-dim">
                                      {t("company_name")}
                                    </span>
                                    <input
                                      required
                                      className="w-full rounded-xl border border-outline bg-background px-3 py-2 text-text outline-none transition focus:border-primary"
                                      value={editCompanyForm.name}
                                      onChange={event =>
                                        setEditCompanyForm(prev =>
                                          prev ? { ...prev, name: event.target.value } : prev,
                                        )
                                      }
                                      disabled={editCompanyBusy}
                                    />
                                  </label>
                                  <label className="space-y-1">
                                    <span className="text-xs font-medium uppercase text-text-dim">
                                      {t("company_logo_url")}
                                    </span>
                                    <input
                                      className="w-full rounded-xl border border-outline bg-background px-3 py-2 text-text outline-none transition focus:border-primary"
                                      value={editCompanyForm.logo_url}
                                      onChange={event =>
                                        setEditCompanyForm(prev =>
                                          prev ? { ...prev, logo_url: event.target.value } : prev,
                                        )
                                      }
                                      disabled={editCompanyBusy}
                                    />
                                  </label>
                                  <label className="flex items-end gap-2 pb-2 text-sm text-text">
                                    <input
                                      type="checkbox"
                                      className="h-4 w-4 rounded border-outline text-primary"
                                      checked={editCompanyForm.allow_password_login}
                                      onChange={event =>
                                        setEditCompanyForm(prev =>
                                          prev
                                            ? {
                                                ...prev,
                                                allow_password_login: event.target.checked,
                                              }
                                            : prev,
                                        )
                                      }
                                      disabled={editCompanyBusy}
                                    />
                                    {t("allow_password_login")}
                                  </label>
                                  <div className="flex items-end gap-2">
                                    <button
                                      type="submit"
                                      disabled={editCompanyBusy}
                                      className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                                      title={t("save_company")}
                                    >
                                      <Save className="h-4 w-4" />
                                    </button>
                                    <button
                                      type="button"
                                      disabled={editCompanyBusy}
                                      onClick={cancelEditingCompany}
                                      className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-outline bg-background text-text transition hover:border-primary disabled:cursor-not-allowed disabled:opacity-50"
                                      title={t("cancel")}
                                    >
                                      <X className="h-4 w-4" />
                                    </button>
                                  </div>
                                  {editCompanyError && (
                                    <div className="rounded-xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger lg:col-span-5">
                                      {editCompanyError}
                                    </div>
                                  )}
                                </form>
                              </td>
                            ) : (
                              <>
                                <td className="px-4 py-3 text-text">{company.id}</td>
                                <td className="px-4 py-3 text-text">{company.name}</td>
                                <td className="px-4 py-3">
                                  {company.logo_url ? (
                                    <img
                                      src={company.logo_url}
                                      alt={company.name}
                                      className="h-10 w-10 rounded border border-outline bg-muted-surface object-contain"
                                    />
                                  ) : (
                                    <span className="text-text-dim">{t("unknown")}</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-text-dim">
                                  {company.allow_password_login
                                    ? t("password_login_enabled")
                                    : t("password_login_disabled")}
                                </td>
                                <td className="px-4 py-3 text-right">
                                  <button
                                    type="button"
                                    onClick={() => startEditingCompany(company)}
                                    className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-outline bg-background text-text transition hover:border-primary"
                                    title={t("edit_company")}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </button>
                                </td>
                              </>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}

          <section className="rounded-2xl border border-outline bg-surface p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between gap-3">
              <h2 className="text-xl font-semibold text-text">{t("users")}</h2>
              <div className="text-sm text-text-dim">
                {loading ? t("loading_users") : t("users_count", { count: sortedUsers.length })}
              </div>
            </div>

            {error ? (
              <div className="rounded-xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
                {error}
              </div>
            ) : loading ? (
              <div className="text-sm text-text-dim">{t("loading_users")}</div>
            ) : (
              <div className="space-y-4">
                {existingEnrollmentSuccess && (
                  <div className="rounded-xl border border-success/30 bg-success/10 px-4 py-3 text-sm text-success">
                    {existingEnrollmentSuccess}
                  </div>
                )}

                <div className="overflow-hidden rounded-xl border border-outline">
                  <table className="min-w-full divide-y divide-outline text-sm">
                    <thead className="bg-muted-surface text-left text-text-dim">
                      <tr>
                        <th className="px-4 py-3 font-medium">{t("username")}</th>
                        <th className="px-4 py-3 font-medium">{t("name")}</th>
                        <th className="px-4 py-3 font-medium">{t("email")}</th>
                        {canEditUsers && (
                          <th className="px-4 py-3 text-right font-medium">{t("actions")}</th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-outline bg-surface">
                      {sortedUsers.map(user => {
                        const isEditing = editingUserId === user.id && editForm !== null;

                        return (
                          <tr key={user.id}>
                            {isEditing ? (
                              <td colSpan={canEditUsers ? 4 : 3} className="px-4 py-4">
                                <form
                                  className="grid gap-3 lg:grid-cols-[1fr_1fr_1fr_1fr_auto]"
                                  onSubmit={event => onEditSubmit(event, user)}
                                >
                                  <div className="space-y-1">
                                    <span className="text-xs font-medium uppercase text-text-dim">
                                      {t("username")}
                                    </span>
                                    <div className="rounded-xl border border-outline bg-muted-surface px-3 py-2 text-sm text-text-dim">
                                      {user.username}
                                    </div>
                                  </div>
                                  <label className="space-y-1">
                                    <span className="text-xs font-medium uppercase text-text-dim">
                                      {t("first_name")}
                                    </span>
                                    <input
                                      className="w-full rounded-xl border border-outline bg-background px-3 py-2 text-text outline-none transition focus:border-primary"
                                      value={editForm.first_name}
                                      onChange={event =>
                                        setEditForm(prev =>
                                          prev ? { ...prev, first_name: event.target.value } : prev,
                                        )
                                      }
                                      disabled={editBusy}
                                    />
                                  </label>
                                  <label className="space-y-1">
                                    <span className="text-xs font-medium uppercase text-text-dim">
                                      {t("last_name")}
                                    </span>
                                    <input
                                      className="w-full rounded-xl border border-outline bg-background px-3 py-2 text-text outline-none transition focus:border-primary"
                                      value={editForm.last_name}
                                      onChange={event =>
                                        setEditForm(prev =>
                                          prev ? { ...prev, last_name: event.target.value } : prev,
                                        )
                                      }
                                      disabled={editBusy}
                                    />
                                  </label>
                                  <label className="space-y-1">
                                    <span className="text-xs font-medium uppercase text-text-dim">
                                      {t("email")}
                                    </span>
                                    <input
                                      className="w-full rounded-xl border border-outline bg-background px-3 py-2 text-text outline-none transition focus:border-primary"
                                      value={editForm.email}
                                      onChange={event =>
                                        setEditForm(prev =>
                                          prev ? { ...prev, email: event.target.value } : prev,
                                        )
                                      }
                                      disabled={editBusy}
                                    />
                                  </label>
                                  <div className="flex items-end gap-2">
                                    <button
                                      type="submit"
                                      disabled={editBusy}
                                      className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                                      title={t("save_user")}
                                    >
                                      <Save className="h-4 w-4" />
                                    </button>
                                    <button
                                      type="button"
                                      disabled={editBusy}
                                      onClick={cancelEditing}
                                      className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-outline bg-background text-text transition hover:border-primary disabled:cursor-not-allowed disabled:opacity-50"
                                      title={t("cancel")}
                                    >
                                      <X className="h-4 w-4" />
                                    </button>
                                  </div>
                                  <label className="space-y-1 lg:col-span-2">
                                    <span className="text-xs font-medium uppercase text-text-dim">
                                      {t("new_password_optional")}
                                    </span>
                                    <input
                                      type="password"
                                      className="w-full rounded-xl border border-outline bg-background px-3 py-2 text-text outline-none transition focus:border-primary"
                                      value={editForm.password}
                                      onChange={event =>
                                        setEditForm(prev =>
                                          prev ? { ...prev, password: event.target.value } : prev,
                                        )
                                      }
                                      disabled={editBusy}
                                    />
                                  </label>
                                  <label className="space-y-1 lg:col-span-2">
                                    <span className="text-xs font-medium uppercase text-text-dim">
                                      {t("confirm_password")}
                                    </span>
                                    <input
                                      type="password"
                                      className="w-full rounded-xl border border-outline bg-background px-3 py-2 text-text outline-none transition focus:border-primary"
                                      value={editForm.confirm_password}
                                      onChange={event =>
                                        setEditForm(prev =>
                                          prev
                                            ? { ...prev, confirm_password: event.target.value }
                                            : prev,
                                        )
                                      }
                                      disabled={editBusy}
                                    />
                                  </label>
                                  {editError && (
                                    <div className="rounded-xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger lg:col-span-5">
                                      {editError}
                                    </div>
                                  )}
                                </form>
                              </td>
                            ) : (
                              <>
                                <td className="px-4 py-3 text-text">{user.username}</td>
                                <td className="px-4 py-3 text-text-dim">
                                  {`${user.first_name ?? ""} ${user.last_name ?? ""}`.trim() ||
                                    t("unknown")}
                                </td>
                                <td className="px-4 py-3 text-text-dim">{user.email ?? "—"}</td>
                                {canEditUsers && (
                                  <td className="px-4 py-3 text-right">
                                    <button
                                      type="button"
                                      onClick={() => startEditing(user)}
                                      className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-outline bg-background text-text transition hover:border-primary"
                                      title={t("edit_user")}
                                    >
                                      <Pencil className="h-4 w-4" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => openExistingEnrollment(user)}
                                      className="ml-2 inline-flex h-9 w-9 items-center justify-center rounded-xl border border-outline bg-background text-text transition hover:border-primary"
                                      title={t("send_enrollment_email")}
                                    >
                                      <Mail className="h-4 w-4" />
                                    </button>
                                    {canChooseCompany && (
                                      <Link
                                        to={`/administration/user-management/${user.id}/permissions/`}
                                        className="ml-2 inline-flex h-9 w-9 items-center justify-center rounded-xl border border-outline bg-background text-text transition hover:border-primary"
                                        title={t("manage_user_permissions")}
                                      >
                                        <ShieldCheck className="h-4 w-4" />
                                      </Link>
                                    )}
                                  </td>
                                )}
                              </>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </section>

          {canImpersonateFromThisFrontend && (
            <section className="rounded-2xl border border-outline bg-surface p-5 shadow-sm">
              <div className="mb-4 flex items-start gap-3">
                <div className="mt-0.5 rounded-xl bg-primary/10 p-2 text-primary">
                  <LogIn className="h-5 w-5" />
                </div>
                <div className="space-y-1">
                  <h2 className="text-xl font-semibold text-text">{t("impersonate_user")}</h2>
                  <p className="text-sm text-text-dim">{t("impersonate_user_help")}</p>
                </div>
              </div>

              <form
                className="grid gap-4 md:grid-cols-[1fr_auto]"
                method="post"
                action={impersonationAction}
                onSubmit={onImpersonationSubmit}
              >
                <input type="hidden" name="redirect" value={impersonationRedirect} />
                <label className="space-y-1">
                  <span className="text-sm font-medium text-text">
                    {t("select_user_to_impersonate")}
                  </span>
                  <select
                    name="user_id"
                    required
                    disabled={loading || sortedUsers.length === 0}
                    className="w-full rounded-xl border border-outline bg-background px-3 py-2 text-text outline-none transition focus:border-primary disabled:cursor-not-allowed disabled:opacity-50"
                    defaultValue=""
                  >
                    <option value="" disabled>
                      {t("select_user_to_impersonate")}
                    </option>
                    {sortedUsers.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.username}
                        {user.email ? ` (${user.email})` : ""}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="flex items-end">
                  <button
                    type="submit"
                    disabled={loading || impersonationBusy || sortedUsers.length === 0}
                    className="inline-flex min-h-10 items-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-medium text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <LogIn className="h-4 w-4" />
                    {t("start_impersonation")}
                  </button>
                </div>
                {impersonationError && (
                  <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 md:col-span-2">
                    {impersonationError}
                  </div>
                )}
              </form>
            </section>
          )}

          {canChooseCompany && (
            <section className="rounded-2xl border border-outline bg-surface p-5 shadow-sm">
              <div className="mb-4 space-y-1">
                <h2 className="text-xl font-semibold text-text">{t("create_company")}</h2>
                <p className="text-sm text-text-dim">{t("create_company_description")}</p>
              </div>

              <Formik<CreateCompanyManagementForm>
                initialValues={emptyCompanyForm}
                onSubmit={onCreateCompanySubmit}
              >
                {({ handleSubmit }) => (
                  <form className="space-y-4" onSubmit={handleSubmit}>
                    <div className="grid gap-4 md:grid-cols-2">
                      <label className="space-y-1">
                        <span className="text-sm font-medium text-text">{t("company_id")}</span>
                        <TextInput
                          name="id"
                          type="number"
                          step={1}
                          wrapped={false}
                          required
                          disabled={createCompanyBusy}
                        />
                      </label>
                      <label className="space-y-1">
                        <span className="text-sm font-medium text-text">{t("company_name")}</span>
                        <TextInput
                          name="name"
                          wrapped={false}
                          required
                          disabled={createCompanyBusy}
                        />
                      </label>
                    </div>

                    <label className="block space-y-1">
                      <span className="text-sm font-medium text-text">{t("company_logo_url")}</span>
                      <TextInput name="logo_url" wrapped={false} disabled={createCompanyBusy} />
                    </label>

                    <div className="flex items-start gap-3 rounded-xl border border-outline bg-muted-surface/30 px-4 py-3">
                      <Switch name="allow_password_login" wrapped={false} />
                      <div className="space-y-1">
                        <div className="text-sm font-medium text-text">
                          {t("allow_password_login")}
                        </div>
                        <div className="text-xs text-text-dim">
                          {t("allow_password_login_description")}
                        </div>
                      </div>
                    </div>

                    {createCompanyError && (
                      <div className="rounded-xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
                        {createCompanyError}
                      </div>
                    )}

                    {createCompanySuccess && (
                      <div className="rounded-xl border border-success/30 bg-success/10 px-4 py-3 text-sm text-success">
                        {createCompanySuccess}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={createCompanyBusy}
                      className="inline-flex rounded-xl bg-primary px-4 py-2 text-sm font-medium text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {createCompanyBusy ? t("creating_company") : t("create_company")}
                    </button>
                  </form>
                )}
              </Formik>
            </section>
          )}

          <section className="rounded-2xl border border-outline bg-surface p-5 shadow-sm">
            <div className="mb-4 space-y-1">
              <h2 className="text-xl font-semibold text-text">{t("create_user")}</h2>
              <p className="text-sm text-text-dim">{t("create_user_description")}</p>
            </div>

            {!canCreateUsers && (
              <div className="mb-4 rounded-xl border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-warning">
                {t("you_do_not_have_permission_to_create_users")}
              </div>
            )}

            {companyError && (
              <div className="mb-4 rounded-xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
                {companyError}
              </div>
            )}

            <Formik<CreateUserForm>
              initialValues={{ ...emptyForm, company_id: defaultCompanyId }}
              enableReinitialize
              onSubmit={onSubmit}
            >
              {({ handleSubmit }) => {
                return (
                  <form className="space-y-4" onSubmit={handleSubmit}>
                    {canChooseCompany && (
                      <div className="grid gap-4">
                        <div className="space-y-1">
                          <span className="text-sm font-medium text-text">{t("company")}</span>
                          <Select
                            name="company_id"
                            wrapped={false}
                            choices={sortedCompanies}
                            displayChoice={company => company.name}
                            disabled={
                              submitBusy ||
                              !canCreateUsers ||
                              companiesLoading ||
                              sortedCompanies.length === 0
                            }
                            readOnly={
                              submitBusy ||
                              !canCreateUsers ||
                              companiesLoading ||
                              sortedCompanies.length === 0
                            }
                          />
                          {companiesLoading && (
                            <div className="text-xs text-text-dim">{t("loading_companies")}</div>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="grid gap-4 md:grid-cols-2">
                      <label className="space-y-1">
                        <span className="text-sm font-medium text-text">{t("username")}</span>
                        <TextInput
                          name="username"
                          wrapped={false}
                          disabled={submitBusy || !canCreateUsers}
                        />
                      </label>
                      <label className="space-y-1">
                        <span className="text-sm font-medium text-text">{t("password")}</span>
                        <TextInput
                          name="password"
                          type="password"
                          wrapped={false}
                          disabled={submitBusy || !canCreateUsers}
                        />
                      </label>
                    </div>

                    <div className="grid gap-4 md:grid-cols-3">
                      <label className="space-y-1">
                        <span className="text-sm font-medium text-text">{t("first_name")}</span>
                        <TextInput
                          name="first_name"
                          wrapped={false}
                          disabled={submitBusy || !canCreateUsers}
                        />
                      </label>
                      <label className="space-y-1">
                        <span className="text-sm font-medium text-text">{t("last_name")}</span>
                        <TextInput
                          name="last_name"
                          wrapped={false}
                          disabled={submitBusy || !canCreateUsers}
                        />
                      </label>
                      <label className="space-y-1">
                        <span className="text-sm font-medium text-text">{t("email")}</span>
                        <TextInput
                          name="email"
                          wrapped={false}
                          disabled={submitBusy || !canCreateUsers}
                        />
                      </label>
                    </div>

                    {submitError && (
                      <div className="rounded-xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
                        {submitError}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={submitBusy || !canCreateUsers}
                      className="inline-flex rounded-xl bg-primary px-4 py-2 text-sm font-medium text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {submitBusy ? t("creating_user") : t("create_user")}
                    </button>
                  </form>
                );
              }}
            </Formik>
          </section>

          <section className="rounded-2xl border border-outline bg-surface p-5 shadow-sm">
            <div className="mb-4 space-y-1">
              <h2 className="text-xl font-semibold text-text">{t("enroll_user")}</h2>
              <p className="text-sm text-text-dim">{t("enroll_user_description")}</p>
            </div>

            {!canCreateUsers && (
              <div className="mb-4 rounded-xl border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-warning">
                {t("you_do_not_have_permission_to_create_users")}
              </div>
            )}

            {companyError && (
              <div className="mb-4 rounded-xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
                {companyError}
              </div>
            )}

            <Formik<EnrollUserForm>
              initialValues={{ ...emptyEnrollForm, company_id: defaultCompanyId }}
              enableReinitialize
              onSubmit={onEnrollSubmit}
            >
              {({ handleSubmit }) => {
                return (
                  <form className="space-y-4" onSubmit={handleSubmit}>
                    {canChooseCompany && (
                      <div className="grid gap-4">
                        <div className="space-y-1">
                          <span className="text-sm font-medium text-text">{t("company")}</span>
                          <Select
                            name="company_id"
                            wrapped={false}
                            choices={sortedCompanies}
                            displayChoice={company => company.name}
                            disabled={
                              enrollBusy ||
                              !canCreateUsers ||
                              companiesLoading ||
                              sortedCompanies.length === 0
                            }
                            readOnly={
                              enrollBusy ||
                              !canCreateUsers ||
                              companiesLoading ||
                              sortedCompanies.length === 0
                            }
                          />
                          {companiesLoading && (
                            <div className="text-xs text-text-dim">{t("loading_companies")}</div>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="grid gap-4 md:grid-cols-2">
                      <label className="space-y-1">
                        <span className="text-sm font-medium text-text">{t("username")}</span>
                        <TextInput
                          name="username"
                          wrapped={false}
                          disabled={enrollBusy || !canCreateUsers}
                        />
                      </label>
                      <label className="space-y-1">
                        <span className="text-sm font-medium text-text">{t("email")}</span>
                        <TextInput
                          name="email"
                          wrapped={false}
                          disabled={enrollBusy || !canCreateUsers}
                        />
                      </label>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                      <label className="space-y-1">
                        <span className="text-sm font-medium text-text">{t("first_name")}</span>
                        <TextInput
                          name="first_name"
                          wrapped={false}
                          disabled={enrollBusy || !canCreateUsers}
                        />
                      </label>
                      <label className="space-y-1">
                        <span className="text-sm font-medium text-text">{t("last_name")}</span>
                        <TextInput
                          name="last_name"
                          wrapped={false}
                          disabled={enrollBusy || !canCreateUsers}
                        />
                      </label>
                    </div>

                    {enrollError && (
                      <div className="rounded-xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
                        {enrollError}
                      </div>
                    )}

                    {enrollSuccess && (
                      <div className="rounded-xl border border-success/30 bg-success/10 px-4 py-3 text-sm text-success">
                        {enrollSuccess}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={enrollBusy || !canCreateUsers}
                      className="inline-flex rounded-xl bg-primary px-4 py-2 text-sm font-medium text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {enrollBusy ? t("enrolling_user") : t("enroll_user")}
                    </button>
                  </form>
                );
              }}
            </Formik>
          </section>
        </div>
      </div>

      {existingEnrollmentUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="existing-enrollment-title"
            className="w-full max-w-lg rounded-2xl border border-outline bg-surface p-5 shadow-xl"
          >
            <div className="space-y-2">
              <h2 id="existing-enrollment-title" className="text-xl font-semibold text-text">
                {t("send_enrollment_email_to_user", {
                  username: existingEnrollmentUser.username,
                })}
              </h2>
              <p className="text-sm text-text-dim">
                {t("send_enrollment_email_confirmation", {
                  email: existingEnrollmentUser.email ?? t("unknown"),
                })}
              </p>
            </div>

            <div className="mt-4 space-y-2 rounded-xl border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-warning">
              <p>{t("enrollment_resets_password_warning")}</p>
              <p>{t("enrollment_onboarding_email_warning")}</p>
            </div>

            {!existingEnrollmentUser.email && (
              <div className="mt-4 rounded-xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
                {t("enrollment_existing_missing_email")}
              </div>
            )}

            {existingEnrollmentError && (
              <div className="mt-4 rounded-xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
                {existingEnrollmentError}
              </div>
            )}

            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                disabled={existingEnrollmentBusy}
                onClick={closeExistingEnrollment}
                className="inline-flex min-h-10 items-center justify-center rounded-xl border border-outline bg-background px-4 py-2 text-sm font-medium text-text transition hover:border-primary disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t("cancel")}
              </button>
              <button
                type="button"
                disabled={existingEnrollmentBusy}
                onClick={confirmExistingEnrollment}
                className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-medium text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Mail className="h-4 w-4" />
                {existingEnrollmentBusy ? t("enrolling_user") : t("send_enrollment_email")}
              </button>
            </div>
          </div>
        </div>
      )}
    </Container>
  );
};

export default UserManagement;
